import { BuiltInActions, BuiltInWorkflows, Errors, Primitives, setAdkCommand } from '@botpress/runtime/internal'

import fs from 'fs/promises'
import path from 'path'
import { AssetsManager, AssetSyncOptions, AssetSyncPlan, AssetSyncResult } from '../assets/index.js'
import { IntegrationManager } from '../integrations/index.js'
import { ParsedIntegration } from '../integrations/types.js'
import { InterfaceManager } from '../interfaces/index.js'
import { ParsedInterface } from '../interfaces/types.js'

import { agentInfoKeyOrder, stringifyWithOrder } from '../utils/json-ordering.js'
import { expandExports } from './expand-exports.js'
import {
  AgentConfig,
  AgentInfo,
  agentInfoSchema,
  Dependencies,
  dependenciesSchema,
  Integration,
  ProjectInfo,
  ProjectState,
  ValidationError,
  ValidationErrorCode,
  ValidationResult,
  ValidationSeverity,
} from './types.js'
import { ValidationErrors } from './validation-errors.js'

export interface AgentProjectOptions {
  noCache?: boolean
  adkCommand?: 'adk-dev' | 'adk-build' | 'adk-deploy'
}

type ConversationDefinition = Primitives.Definitions.ConversationDefinition
type KnowledgeDefinition = Primitives.Definitions.KnowledgeDefinition
type TriggerDefinition = Primitives.Definitions.TriggerDefinition
type WorkflowDefinition = Primitives.Definitions.WorkflowDefinition
type ActionDefinition = Primitives.Definitions.ActionDefinition
type TableDefinition = Primitives.Definitions.TableDefinition
type PrimitiveDefinition = Primitives.Definitions.PrimitiveDefinition

export type PrimitiveReference<T extends PrimitiveDefinition = PrimitiveDefinition> = {
  path: string
  export: string
  definition: T
}

export class AgentProject {
  private static _projectCache = new Map<string, AgentProject>()

  private _options: AgentProjectOptions
  private _path: string
  private _config?: AgentConfig
  private _dependencies?: Dependencies
  private _agentInfo?: AgentInfo
  private _state: ProjectState = ProjectState.Unloaded
  private _errors: ValidationError[] = []
  private _warnings: ValidationError[] = []

  private _lastBuildTime?: Date
  private _integrations?: ParsedIntegration[]
  private _interfaces?: ParsedInterface[]
  private _integrationManager: IntegrationManager
  private _interfaceManager: InterfaceManager
  private _assetsManager: AssetsManager
  private _conversations: PrimitiveReference<ConversationDefinition>[] = []
  private _knowledge: PrimitiveReference<KnowledgeDefinition>[] = []
  private _triggers: PrimitiveReference<TriggerDefinition>[] = []
  private _workflows: PrimitiveReference<WorkflowDefinition>[] = []
  private _actions: PrimitiveReference<ActionDefinition>[] = []
  private _tables: PrimitiveReference<TableDefinition>[] = []

  constructor(projectPath: string, options: AgentProjectOptions = {}) {
    this._options = options
    this._path = path.resolve(projectPath)
    this._integrationManager = new IntegrationManager({
      noCache: options.noCache,
    })
    this._interfaceManager = new InterfaceManager({
      noCache: options.noCache,
    })
    this._assetsManager = new AssetsManager({
      projectPath: this._path,
    })
  }

  // Static factory methods
  static async load(projectPath: string, options: AgentProjectOptions = {}): Promise<AgentProject> {
    const resolvedPath = path.resolve(projectPath)

    // Check cache unless explicitly disabled
    if (!options.noCache) {
      const cached = AgentProject._projectCache.get(resolvedPath)
      if (cached) {
        return cached
      }
    }

    // Create and load new project
    const project = new AgentProject(resolvedPath, options)
    await project.reload()

    // Cache the project unless caching is disabled
    if (!options.noCache) {
      AgentProject._projectCache.set(resolvedPath, project)
    }

    return project
  }

  // Static method to clear the cache
  static clearCache(): void {
    AgentProject._projectCache.clear()
  }

  // Static method to remove a specific project from cache
  static clearCacheForPath(projectPath: string): void {
    const resolvedPath = path.resolve(projectPath)
    AgentProject._projectCache.delete(resolvedPath)
  }

  // Public properties
  get path(): string {
    return this._path
  }

  get conversations(): PrimitiveReference<ConversationDefinition>[] {
    return this._conversations
  }

  get knowledge(): PrimitiveReference<KnowledgeDefinition>[] {
    return this._knowledge
  }

  get triggers(): PrimitiveReference<TriggerDefinition>[] {
    return this._triggers
  }

  get workflows(): PrimitiveReference<WorkflowDefinition>[] {
    return this._workflows
  }

  get actions(): PrimitiveReference<ActionDefinition>[] {
    return this._actions
  }

  get tables(): PrimitiveReference<TableDefinition>[] {
    return this._tables
  }

  get config(): AgentConfig | undefined {
    return this._config
  }

  get dependencies(): Dependencies | undefined {
    return this._dependencies
  }

  get agentInfo(): AgentInfo | undefined {
    return this._agentInfo
  }

  get state(): ProjectState {
    return this._state
  }

  get info(): ProjectInfo {
    return {
      path: this._path,
      config: this._config!,
      dependencies: this._dependencies || {
        integrations: {},
      },
      agentInfo: this._agentInfo,
      state: this._state,
      lastBuildTime: this._lastBuildTime,
      errors: [...this._errors],
      warnings: [...this._warnings],
      errorCount: this._errors.filter((e) => e.severity === ValidationSeverity.ERROR).length,
      warningCount: this._warnings.filter((e) => e.severity === ValidationSeverity.WARNING).length,
      infoCount: [...this._errors, ...this._warnings].filter((e) => e.severity === ValidationSeverity.INFO).length,
    }
  }

  // Core methods
  async reload(): Promise<void> {
    this._state = ProjectState.Loading

    try {
      // Clear previous state
      this._conversations = []
      this._knowledge = []
      this._triggers = []
      this._workflows = []
      this._actions = []
      this._tables = []
      this._errors = []
      this._warnings = []

      // Validate project structure
      const validation = await this.validate()
      this._errors = validation.errors
      this._warnings = validation.warnings

      if (!validation.valid) {
        this._state = ProjectState.Error

        throw new Error(`Project validation failed: ${validation.errors[0]?.message}`)
      }

      // Load configuration
      await this.loadConfig()
      await this.loadAgentInfo()

      this._integrationManager = new IntegrationManager({
        noCache: this._options.noCache,
        workspaceId: this._agentInfo?.workspaceId,
      })

      this._interfaceManager = new InterfaceManager({
        noCache: this._options.noCache,
        workspaceId: this._agentInfo?.workspaceId,
      })

      this._assetsManager = new AssetsManager({
        projectPath: this._path,
        workspaceId: this._agentInfo?.workspaceId,
        botId: this._agentInfo?.botId,
      })

      await this.loadDependencies()
      await this.loadBuiltInWorkflows()
      await this.loadBuiltInActions()
      await this.loadAgentPrimitives()

      // Update assets manager with agent info if available
      if (this._agentInfo) {
        this._assetsManager = new AssetsManager({
          projectPath: this._path,
          botId: this._agentInfo.botId,
          workspaceId: this._agentInfo.workspaceId,
        })
      }

      if (this._errors.length > 0) {
        this._state = ProjectState.Error
      } else {
        this._state = ProjectState.Ready
      }
    } catch (error) {
      this._state = ProjectState.Error

      throw error
    }
  }

  async validate(): Promise<ValidationResult> {
    const errors: ValidationError[] = []
    const warnings: ValidationError[] = []
    const info: ValidationError[] = []

    try {
      // Check if directory exists
      await fs.access(this._path)

      // Check for required files
      const requiredFiles = ['agent.config.ts', 'dependencies.json']
      for (const file of requiredFiles) {
        try {
          await fs.access(path.join(this._path, file))
        } catch {
          errors.push(ValidationErrors.requiredFileMissing(file))
        }
      }

      // Check for agent.json (optional, but warn if missing for deployed projects)
      try {
        await fs.access(path.join(this._path, 'agent.json'))
      } catch {
        // agent.json is optional, but we add an informational message
        info.push({
          $type: 'ValidationError',
          code: ValidationErrorCode.MISSING_REQUIRED_FIELD,
          severity: ValidationSeverity.INFO,
          message: 'agent.json not found - this file will be required for deployment and remote operations',
          file: 'agent.json',
          hint: 'Create agent.json with botId and workspaceId after deploying your agent',
        })
      }

      // Check directory structure
      const expectedDirs = ['actions', 'workflows', 'conversations', 'assets']
      for (const dir of expectedDirs) {
        try {
          const stats = await fs.stat(path.join(this._path, dir))
          if (!stats.isDirectory()) {
            warnings.push(ValidationErrors.invalidStructure(dir, 'directory'))
          }
        } catch {
          // Directory doesn't exist, which is okay - they're optional
        }
      }
    } catch (error) {
      errors.push(ValidationErrors.directoryAccessError(this._path, String(error)))
    }

    // Filter by severity
    const errorsBySeverity = {
      errors: errors.filter((e) => e.severity === ValidationSeverity.ERROR),
      warnings: [...warnings, ...errors.filter((e) => e.severity === ValidationSeverity.WARNING)],
      info: [...info, ...errors.filter((e) => e.severity === ValidationSeverity.INFO)],
    }

    return {
      valid: errorsBySeverity.errors.length === 0,
      errors: errorsBySeverity.errors,
      warnings: errorsBySeverity.warnings,
      info: errorsBySeverity.info,
      errorCount: errorsBySeverity.errors.length,
      warningCount: errorsBySeverity.warnings.length,
      infoCount: errorsBySeverity.info.length,
    }
  }

  get integrations(): ParsedIntegration[] {
    if (!this._integrations) {
      throw new Error('Integrations not loaded')
    }

    return this._integrations!
  }

  get interfaces(): ParsedInterface[] {
    if (!this._interfaces) {
      throw new Error('Interfaces not loaded')
    }
    return this._interfaces!
  }

  // Integration management methods
  async getIntegrations(): Promise<Integration[]> {
    if (!this._dependencies || !this._integrations) {
      throw new Error('Project not loaded')
    }

    // Convert ParsedIntegration to Integration format
    return this._integrations.map((parsed) => ({
      name: parsed.alias,
      version: parsed.ref.version,
      workspace: parsed.ref.workspace,
      connection: parsed.connection,
      config: parsed.config,
      installed: false, // This would be determined by checking workspace
      installedVersion: undefined,
      hasChannels: parsed.definition?.channels && Object.keys(parsed.definition.channels).length > 0,
    }))
  }

  // Asset management methods
  async createAssetSyncPlan(): Promise<AssetSyncPlan> {
    if (this._state !== ProjectState.Ready) {
      throw new Error('Project must be in Ready state to create asset sync plan')
    }

    this.requiresAgentInfo('create asset sync plan')
    return await this._assetsManager.createSyncPlan()
  }

  async syncAssets(options?: AssetSyncOptions): Promise<AssetSyncResult> {
    if (this._state !== ProjectState.Ready) {
      throw new Error('Project must be in Ready state to sync assets')
    }

    this.requiresAgentInfo('sync assets')
    const plan = await this._assetsManager.createSyncPlan()
    return await this._assetsManager.executeSync(plan, options)
  }

  async hasAssetsDirectory(): Promise<boolean> {
    return await this._assetsManager.hasAssetsDirectory()
  }

  get assetsManager(): AssetsManager {
    return this._assetsManager
  }

  // Agent info management methods
  async createAgentInfo(info: AgentInfo): Promise<void> {
    const agentPath = path.join(this._path, 'agent.json')
    const agentContent = stringifyWithOrder(info, agentInfoKeyOrder)
    await fs.writeFile(agentPath, agentContent)
    this._agentInfo = info
  }

  async updateAgentInfo(updates: Partial<AgentInfo>): Promise<void> {
    if (!this._agentInfo) {
      throw new Error('No agent.json found. Use createAgentInfo() first.')
    }

    const updatedInfo = { ...this._agentInfo, ...updates }
    const agentPath = path.join(this._path, 'agent.json')
    const agentContent = stringifyWithOrder(updatedInfo, agentInfoKeyOrder)
    await fs.writeFile(agentPath, agentContent)
    this._agentInfo = updatedInfo
  }

  requiresAgentInfo(operation: string): void {
    if (!this._agentInfo?.botId) {
      throw new Error(
        `Operation "${operation}" requires a bot ID. ` +
          'Please create agent.json with botId and workspaceId after deploying your agent.'
      )
    }
  }

  // Private helper methods
  private async loadConfig(): Promise<void> {
    try {
      // Bust module cache to ensure fresh config on reload
      const configPath = path.join(this._path, 'agent.config.ts')
      const configModule = await import(`${configPath}?t=${Date.now()}`)
      if (!configModule || !configModule.default) {
        this._errors.push({
          $type: 'ValidationError',
          code: ValidationErrorCode.INVALID_CONFIG_SCHEMA,
          severity: ValidationSeverity.ERROR,
          message: 'agent.config.ts does not export a default configuration object',
          file: 'agent.config.ts',
        })
        return
      }

      // Check if the config was created with defineConfig
      const { isAgentConfig } = await import('@botpress/runtime/internal')
      if (!isAgentConfig(configModule.default)) {
        this._errors.push({
          $type: 'ValidationError',
          code: ValidationErrorCode.INVALID_CONFIG_SCHEMA,
          severity: ValidationSeverity.ERROR,
          message:
            'agent.config.ts must export the result of defineConfig(). ' +
            "Example: export default defineConfig({ name: 'my-agent', ... })",
          file: 'agent.config.ts',
          hint: 'Wrap your config object with defineConfig() from @botpress/runtime',
        })
        return
      }

      this._config = configModule.default as AgentConfig
    } catch (error) {
      this._errors.push({
        $type: 'ValidationError',
        code: ValidationErrorCode.INVALID_CONFIG_SYNTAX,
        severity: ValidationSeverity.ERROR,
        message: `Failed to load agent.config.ts: ${(error as Error).message}`,
        file: 'agent.config.ts',
      })
    }
  }

  private async loadDependencies(): Promise<void> {
    try {
      // Load dependencies.json
      const depsPath = path.join(this._path, 'dependencies.json')
      const depsContent = await fs.readFile(depsPath, 'utf-8')

      // Parse JSON
      let depsData: any
      try {
        depsData = JSON.parse(depsContent)
      } catch (parseError) {
        this._errors.push({
          $type: 'ValidationError',
          code: ValidationErrorCode.INVALID_DEPENDENCIES_SYNTAX,
          severity: ValidationSeverity.ERROR,
          message: `Invalid JSON syntax in dependencies.json: ${(parseError as Error).message}`,
          file: 'dependencies.json',
        })
        this._dependencies = { integrations: {} }
        this._integrations = []
        this._interfaces = []
        return
      }

      // Validate against schema
      const validationResult = dependenciesSchema.safeParse(depsData)
      if (!validationResult.success) {
        const errors = validationResult.error.errors
        for (const error of errors) {
          this._errors.push({
            $type: 'ValidationError',
            code: ValidationErrorCode.INVALID_DEPENDENCIES_SCHEMA,
            severity: ValidationSeverity.ERROR,
            message: `Invalid dependencies.json: ${error.path.join('.')} - ${JSON.stringify(error, null, 2)}`,
            file: 'dependencies.json',
            hint: error.code === 'invalid_type' ? `Expected ${error.expected}, but got ${error.received}` : undefined,
          })
        }
        this._dependencies = { integrations: {} }
        this._integrations = []
        this._interfaces = []
        return
      }

      this._dependencies = validationResult.data

      // Validate and load integrations and interfaces
      if (this._dependencies) {
        const [intRes, ifRes] = await Promise.all([
          this._integrationManager.loadIntegrations(this._dependencies),
          this._interfaceManager.loadInterfaces(this._dependencies),
        ])
        this._integrations = intRes.integrations
        this._interfaces = ifRes.interfaces

        // Add errors and warnings
        this._errors.push(...intRes.errors, ...ifRes.errors)
        this._warnings.push(...intRes.warnings, ...ifRes.warnings)
      }
    } catch (error) {
      if ((error as any).code === 'ENOENT') {
        // If dependencies.json doesn't exist, use empty dependencies
        this._dependencies = {
          integrations: {},
        }
        this._integrations = []
        this._interfaces = []
      } else {
        // Re-throw other errors
        throw error
      }
    }
  }

  private async loadAgentInfo(): Promise<void> {
    try {
      // Load agent.json
      const agentPath = path.join(this._path, 'agent.json')
      const agentContent = await fs.readFile(agentPath, 'utf-8')

      // Parse JSON
      let agentData: any
      try {
        agentData = JSON.parse(agentContent)
      } catch (parseError) {
        this._errors.push({
          $type: 'ValidationError',
          code: ValidationErrorCode.INVALID_CONFIG_SYNTAX,
          severity: ValidationSeverity.ERROR,
          message: `Invalid JSON syntax in agent.json: ${(parseError as Error).message}`,
          file: 'agent.json',
        })
      }

      // Validate agent.json structure
      const validationResult = agentInfoSchema.safeParse(agentData)
      if (!validationResult.success) {
        const errors = validationResult.error.errors
        for (const error of errors) {
          // Check if this is a missing botId or workspaceId error
          const isBotIdError = error.path.includes('botId')
          const isWorkspaceIdError = error.path.includes('workspaceId')
          const isLinkingError = (isBotIdError || isWorkspaceIdError) && error.code === 'invalid_type'

          this._errors.push({
            $type: 'ValidationError',
            code: isLinkingError ? ValidationErrorCode.AGENT_NOT_LINKED : ValidationErrorCode.INVALID_CONFIG_SCHEMA,
            severity: ValidationSeverity.ERROR,
            message: `Invalid agent.json: ${error.path.join('.')} - ${error.message}`,
            file: 'agent.json',
            hint:
              error.code === 'invalid_type'
                ? `Expected ${error.expected}, but got ${error.received}`
                : 'Ensure agent.json contains valid botId and workspaceId',
          })
        }
      }

      this._agentInfo = validationResult.data
    } catch (error) {
      // agent.json is optional, but may be required for certain operations
      this._agentInfo = undefined
    }
  }

  private async loadBuiltInWorkflows(): Promise<void> {
    for (const wf of Object.values(BuiltInWorkflows)) {
      const definition = Primitives.Definitions.getDefinition(wf)
      if (Primitives.Definitions.isWorkflowDefinition(definition)) {
        this._workflows.push({
          definition,
          export: 'default',
          path: '<adk:builtin>',
        })
      }
    }
  }

  private async loadBuiltInActions(): Promise<void> {
    for (const action of Object.values(BuiltInActions)) {
      const definition = Primitives.Definitions.getDefinition(action)
      if (Primitives.Definitions.isActionDefinition(definition)) {
        this._actions.push({
          definition,
          export: 'default',
          path: '<adk:builtin>',
        })
      }
    }
  }

  private async registerDataSourceWorkflows(knowledgeBase: any, kbPath: string, kbExport: string): Promise<void> {
    try {
      // Each knowledge base has data sources with sync workflows
      if (!knowledgeBase.sources || !Array.isArray(knowledgeBase.sources)) {
        return
      }

      for (const source of knowledgeBase.sources) {
        if (!source.syncWorkflow) {
          continue
        }

        const workflowDefinition = source.syncWorkflow.getDefinition()

        // Check if workflow already exists
        const existing = this._workflows.find((p) => p.definition.name === workflowDefinition.name)

        if (existing) {
          // Skip if already registered
          continue
        }

        // Register the data source's sync workflow
        this._workflows.push({
          definition: workflowDefinition,
          export: `${kbExport}.sources[${knowledgeBase.sources.indexOf(source)}].syncWorkflow`,
          path: kbPath,
        })
      }
    } catch (error) {
      console.warn(`Failed to register data source workflows for ${kbPath}:`, error)
    }
  }

  /**
   * Get list of channels from a channel specification (single, array, or glob)
   * For glob '*', we need to check against all integration channels - but for validation
   * we treat it as a special marker
   */
  private getChannelsList(channelSpec: string | string[]): string[] {
    if (channelSpec === '*') {
      return ['*'] // Glob marker - matches everything
    } else if (Array.isArray(channelSpec)) {
      return channelSpec
    } else {
      return [channelSpec]
    }
  }

  private async loadAgentPrimitives(): Promise<void> {
    // Set the ADK command in the runtime environment
    if (this._options.adkCommand) {
      setAdkCommand(this._options.adkCommand)
    }

    try {
      const src = path.join(this._path, 'src')

      if (!(await fs.stat(src).catch(() => false))) {
        this._errors.push({
          $type: 'ValidationError',
          code: ValidationErrorCode.INVALID_STRUCTURE,
          severity: ValidationSeverity.ERROR,
          message: `\`src\` directory not found at expected path: ${src}`,
        })
        return
      }

      const allFiles = await fs.readdir(src, {
        withFileTypes: true,
        recursive: true,
      })

      for (const file of allFiles.filter((x) => x.isFile())) {
        const filename = file.name
        const absolutePath = path.join(file.parentPath, file.name)
        const relPath = path.relative(this.path, absolutePath)

        if (filename.toLowerCase().endsWith('.d.ts')) {
          // Skip TypeScript declaration files
          continue
        }

        if (filename.toLowerCase().endsWith('.test.ts') || filename.toLowerCase().endsWith('.test.js')) {
          // Skip test files
          continue
        }

        if (!filename.toLowerCase().endsWith('.js') && !filename.toLowerCase().endsWith('.ts')) {
          continue // Only process .js or .ts files
        }

        if (file.isSymbolicLink()) {
          this._errors.push({
            $type: 'ValidationError',
            code: ValidationErrorCode.INVALID_FILE_TYPE,
            severity: ValidationSeverity.WARNING,
            message: `Skipping symbolic link in conversations directory: ${relPath}`,
            file: relPath,
          })
          continue
        }

        try {
          const expandedExports = await expandExports({
            absolutePath,
            relPath,
            filename,
            onWarning: (warning) => this._warnings.push(warning),
          })

          for (const key of Object.keys(expandedExports)) {
            let definition
            try {
              definition = Primitives.Definitions.getDefinition(expandedExports[key])
            } catch (error) {
              // This might catch errors from getDefinition itself
              if (Errors.isAdkError(error)) {
                this._warnings.push({
                  $type: 'ValidationError',
                  code: ValidationErrorCode.INVALID_PRIMITIVE_DEFINITION,
                  severity: ValidationSeverity.WARNING,
                  message: error.message,
                  file: relPath,
                  hint: `Check the primitive definition in ${filename} -> ${key}`,
                })
                continue
              }
              // Re-throw if it's not an ADK error
              throw error
            }

            if (Primitives.Definitions.isConversationDefinition(definition)) {
              // Check for overlapping channel definitions
              const overlapping = this._conversations.find((p) => {
                const existingChannels = this.getChannelsList(p.definition.channel)
                const newChannels = this.getChannelsList(definition.channel)

                // Check if any channel overlaps
                return existingChannels.some((ch) => newChannels.includes(ch))
              })

              if (overlapping) {
                this._warnings.push({
                  $type: 'ValidationError',
                  code: ValidationErrorCode.DUPLICATE_PRIMITIVE,
                  severity: ValidationSeverity.WARNING,
                  message: `Overlapping conversation channels found: ${filename} -> ${key} overlaps with ${overlapping.path} -> ${overlapping.export}`,
                  file: relPath,
                })
                continue
              }
              this._conversations.push({
                definition,
                export: key,
                path: relPath,
              })
            } else if (Primitives.Definitions.isKnowledgeDefinition(definition)) {
              const existing = this._knowledge.find((p) => p.definition.name === definition.name)

              if (existing) {
                this._warnings.push({
                  $type: 'ValidationError',
                  code: ValidationErrorCode.DUPLICATE_PRIMITIVE,
                  severity: ValidationSeverity.WARNING,
                  message: `Duplicate knowledge definition found: ${filename} -> ${key} (already defined in ${existing.path} -> ${existing.export})`,
                  file: relPath,
                })
                continue
              }

              this._knowledge.push({
                definition,
                export: key,
                path: relPath,
              })

              // Register data source sync workflows
              await this.registerDataSourceWorkflows(expandedExports[key], relPath, key)
            } else if (Primitives.Definitions.isTriggerDefinition(definition)) {
              const existing = this._triggers.find((p) => p.definition.name === definition.name)
              if (existing) {
                this._warnings.push({
                  $type: 'ValidationError',
                  code: ValidationErrorCode.DUPLICATE_PRIMITIVE,
                  severity: ValidationSeverity.WARNING,
                  message: `Duplicate trigger definition found: ${filename} -> ${key} (already defined in ${existing.path} -> ${existing.export})`,
                  file: relPath,
                })
                continue
              }
              this._triggers.push({
                definition,
                export: key,
                path: relPath,
              })
            } else if (Primitives.Definitions.isWorkflowDefinition(definition)) {
              const existing = this._workflows.find((p) => p.definition.name === definition.name)
              if (existing) {
                this._warnings.push({
                  $type: 'ValidationError',
                  code: ValidationErrorCode.DUPLICATE_PRIMITIVE,
                  severity: ValidationSeverity.WARNING,
                  message: `Duplicate workflow definition found: ${filename} -> ${key} (already defined in ${existing.path} -> ${existing.export})`,
                  file: relPath,
                })
                continue
              }
              this._workflows.push({
                definition,
                export: key,
                path: relPath,
              })
            } else if (Primitives.Definitions.isActionDefinition(definition)) {
              const existing = this._actions.find((p) => p.definition.name === definition.name)
              if (existing) {
                this._warnings.push({
                  $type: 'ValidationError',
                  code: ValidationErrorCode.DUPLICATE_PRIMITIVE,
                  severity: ValidationSeverity.WARNING,
                  message: `Duplicate action definition found: ${filename} -> ${key} (already defined in ${existing.path} -> ${existing.export})`,
                  file: relPath,
                })
                continue
              }
              this._actions.push({
                definition,
                export: key,
                path: relPath,
              })
            } else if (Primitives.Definitions.isTableDefinition(definition)) {
              const existing = this._tables.find((p) => p.definition.name === definition.name)
              if (existing) {
                this._warnings.push({
                  $type: 'ValidationError',
                  code: ValidationErrorCode.DUPLICATE_PRIMITIVE,
                  severity: ValidationSeverity.WARNING,
                  message: `Duplicate table definition found: ${filename} -> ${key} (already defined in ${existing.path} -> ${existing.export})`,
                  file: relPath,
                })
                continue
              }
              this._tables.push({
                definition,
                export: key,
                path: relPath,
              })
            }
          }
        } catch (error) {
          // Check if it's an ADK error from expandExports
          if (Errors.isAdkError(error)) {
            // Error is already handled by expandExports, skip this file
            continue
          }

          // Log other import errors with context
          this._warnings.push({
            $type: 'ValidationError',
            code: ValidationErrorCode.IMPORT_ERROR,
            severity: ValidationSeverity.WARNING,
            message: `Failed to import primitive from ${relPath}: ${error instanceof Error ? error.message : String(error)}`,
            file: relPath,
            hint: 'Ensure the file exports valid primitives and has no syntax errors',
          })
        }
      }
    } catch (error) {
      throw error
    }
  }
}
